import React, { useState, useEffect, useMemo } from 'react';
import {
  X, Sparkles, Copy, RefreshCw, ExternalLink, Car, Gauge, TrendingUp, ShieldCheck,
  MessageCircle, FileDown, CheckCircle2, AlertTriangle, Link2, Heart, Clock, Calendar,
  Camera, Upload, Trash2, Eye, Globe,
} from 'lucide-react';
import { carsysApi } from '../api/carsysApi';
import { useT } from '../context/LanguageContext';
import { toast } from 'sonner';

const enc = encodeURIComponent;
const slug = (s) => (s || '').toString().toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
const eur = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const eur2 = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });

// Talen voor de AI-advertentie (vertaalt de hele advertentie natuurlijk, geen letterlijke vertaling)
const AD_TALEN = [
  { code: 'nl', label: 'Nederlands', vlag: '🇳🇱' },
  { code: 'en', label: 'Engels', vlag: '🇬🇧' },
  { code: 'de', label: 'Duits', vlag: '🇩🇪' },
  { code: 'fr', label: 'Frans', vlag: '🇫🇷' },
  { code: 'pl', label: 'Pools', vlag: '🇵🇱' },
  { code: 'es', label: 'Spaans', vlag: '🇪🇸' },
  { code: 'it', label: 'Italiaans', vlag: '🇮🇹' },
  { code: 'tr', label: 'Turks', vlag: '🇹🇷' },
];

export default function VerkoopAdvertentie({ auto, onClose }) {
  const { t, lang } = useT();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [platform, setPlatform] = useState('marktplaats');
  const [tab, setTab] = useState('tekst'); // tekst | specs | foto | prijs
  const [stijl, setStijl] = useState('standaard');
  const [adLang, setAdLang] = useState(AD_TALEN.some(l => l.code === lang) ? lang : 'nl');
  const [fotos, setFotos] = useState(() => (auto.fotos || []).map(f => (typeof f === 'string' ? { dataUrl: f } : f)));
  const fileRef = React.useRef(null);
  const reqSeq = React.useRef(0);      // volgnummer: alleen de laatste request mag het resultaat zetten
  const started = React.useRef(false); // voorkomt dubbele initiële generatie (React StrictMode)

  const STIJLEN = [
    { id: 'standaard', label: t('Standaard', 'Standaard') },
    { id: 'zakelijk', label: t('Zakelijk', 'Zakelijk') },
    { id: 'enthousiast', label: t('Enthousiast', 'Enthousiast') },
    { id: 'kort', label: t('Kort', 'Kort') },
  ];

  const term = useMemo(() => [auto.merk, auto.model, auto.bouwjaar].filter(Boolean).join(' ').trim(), [auto]);

  // Voertuigtype-bewuste vergelijk-links + platformnaam (auto vs camper/aanhanger/scooter/brommer etc.)
  const isCarLike = !auto.voertuigType || ['Personenauto', 'Bestelbus', 'Oldtimer'].includes(auto.voertuigType);
  const PLATFORMS = useMemo(() => [
    { id: 'marktplaats', naam: 'Marktplaats', kleur: '#3f9c35' },
    { id: 'autoscout24', naam: isCarLike ? 'AutoScout24' : t('Zakelijk', 'Zakelijk'), kleur: '#1f5fbf' },
    { id: 'facebook', naam: 'Facebook Marketplace', kleur: '#1877f2' },
  ], [isCarLike, t]);

  const bronId = auto.id || (auto.kenteken ? `adhoc-${auto.kenteken}` : `adhoc-${term}`);
  const [showroom, setShowroom] = useState(null); // {id, status} of de showroom-advertentie
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    carsysApi.getShowroomStatusForBron(bronId).then(r => { if (r && r.status) setShowroom(r); }).catch(() => {});
  }, [bronId]);

  const publiceer = async (status) => {
    if (!huidig) { toast.error(t('Genereer eerst de advertentie', 'Genereer eerst de advertentie')); return; }
    setPublishing(true);
    try {
      const r = await carsysApi.publishShowroom({
        type: 'occasion', bronId, status,
        titel: huidig.titel, aiTekst: huidig.tekst, prijs,
        kenteken: auto.kenteken, merk: auto.merk, model: auto.model, bouwjaar: auto.bouwjaar,
        kmStand: auto.kmStand, brandstof: auto.brandstof, kleur: auto.kleur,
        apk: result?.apkStatus || '', specificaties: result?.specificaties || [],
        fotos: fotos.map(f => f.dataUrl),
      });
      setShowroom(r);
      toast.success(status === 'Concept' ? t('Opgeslagen als concept', 'Opgeslagen als concept') : t('Gepubliceerd in showroom!', 'Gepubliceerd in showroom!'),
        { description: r.publicUrl });
    } catch (e) {
      toast.error(t('Publiceren mislukt', 'Publiceren mislukt'), { description: e.response?.data?.detail || e.message });
    } finally { setPublishing(false); }
  };

  const verwijderUitShowroom = async () => {
    if (!showroom?.id) return;
    setPublishing(true);
    try { await carsysApi.deleteShowroom(showroom.id); setShowroom(null); toast.success(t('Verwijderd uit showroom', 'Verwijderd uit showroom')); }
    catch (e) { toast.error(t('Mislukt', 'Mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setPublishing(false); }
  };

  const links = useMemo(() => {
    const merk = slug(auto.merk), model = slug(auto.model);
    const jaar = auto.bouwjaar ? `?fregfrom=${auto.bouwjaar}&fregto=${auto.bouwjaar}` : '';
    const mp = `https://www.marktplaats.nl/q/${enc(term || merk)}/`;
    if (isCarLike) {
      return [
        { id: 'mp', label: t('Bekijk op Marktplaats', 'Bekijk op Marktplaats'), url: mp, kleur: '#3f9c35' },
        { id: 'as24', label: t('Bekijk op AutoScout24', 'Bekijk op AutoScout24'), url: merk ? `https://www.autoscout24.nl/lst/${merk}${model ? '/' + model : ''}${jaar}` : 'https://www.autoscout24.nl', kleur: '#1f5fbf' },
        { id: 'gp', label: t('Bekijk op Gaspedaal', 'Bekijk op Gaspedaal'), url: merk ? `https://www.gaspedaal.nl/${merk}${model ? '/' + model : ''}` : 'https://www.gaspedaal.nl', kleur: '#dc2626' },
      ];
    }
    // Niet-auto types (camper/aanhanger/scooter/brommer/motor/vrachtwagen): Marktplaats-zoek + Google prijsvergelijk
    return [
      { id: 'mp', label: t('Bekijk op Marktplaats', 'Bekijk op Marktplaats'), url: mp, kleur: '#3f9c35' },
      { id: 'google', label: t('Vergelijk prijzen (Google)', 'Vergelijk prijzen (Google)'), url: `https://www.google.com/search?q=${enc(`${term} te koop`)}`, kleur: '#4285f4' },
    ];
  }, [auto, term, isCarLike, t]);

  const genereer = async (gekozenStijl, fotosArg, taalArg) => {
    const seq = ++reqSeq.current; // markeer deze als de meest recente aanvraag
    setLoading(true);
    try {
      const imgs = (fotosArg ?? fotos).map(f => f.dataUrl);
      const r = await carsysApi.generateAdvertentie({
        merk: auto.merk, model: auto.model, bouwjaar: auto.bouwjaar,
        kmStand: auto.kmStand, verkoopPrijs: auto.verkoopPrijs, inkoopPrijs: auto.inkoopPrijs,
        kenteken: auto.kenteken, brandstof: auto.brandstof, kleur: auto.kleur, notitie: auto.notitie,
        kostenposten: auto.kostenposten, lang: taalArg || adLang, stijl: gekozenStijl || stijl,
        voertuigType: auto.voertuigType || '',
        pluspunten: auto.pluspunten || [], schadepunten: auto.schadepunten || '', onderhoudshistorie: auto.onderhoudshistorie || '',
        fotos: imgs,
      });
      if (seq === reqSeq.current) setResult(r); // negeer verouderde antwoorden (taal-wissel race)
    } catch (e) {
      if (seq === reqSeq.current) toast.error(t('Genereren mislukt', 'Genereren mislukt'), { description: e.response?.data?.detail || e.message });
    } finally { if (seq === reqSeq.current) setLoading(false); }
  };

  const vertaalNaar = (code) => { setAdLang(code); genereer(undefined, undefined, code); };

  // Foto's verkleinen client-side (max 1280px, jpeg 0.72) → kleine payload
  const verkleinFoto = (file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const max = 1280;
        let { width, height } = img;
        if (width > max || height > max) {
          const r = Math.min(max / width, max / height);
          width = Math.round(width * r); height = Math.round(height * r);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.72));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

  const onFotosKiezen = async (e) => {
    const files = Array.from(e.target.files || []).filter(f => /image\/(jpeg|png|webp)/.test(f.type));
    if (!files.length) return;
    const verkleind = await Promise.all(files.map(verkleinFoto));
    const nieuw = [...fotos, ...verkleind.map(dataUrl => ({ dataUrl }))].slice(0, 6);
    setFotos(nieuw);
    if (fileRef.current) fileRef.current.value = '';
    toast.success(t('Foto\'s toegevoegd — analyseren met AI…', 'Foto\'s toegevoegd — analyseren met AI…'));
    genereer(undefined, nieuw);
  };

  const verwijderFoto = (i) => {
    const nieuw = fotos.filter((_, idx) => idx !== i);
    setFotos(nieuw);
  };

  useEffect(() => { if (started.current) return; started.current = true; genereer(); /* eslint-disable-next-line */ }, []);

  const huidig = result?.platforms?.[platform];
  const mp = result?.marktprijs;
  const winst = result?.winst;
  const beoordeling = result?.beoordeling;
  const fotoanalyse = result?.fotoanalyse;
  const onderhoudswaarde = result?.onderhoudswaarde;
  const concurrentie = result?.concurrentie;
  const vk = result?.verkoopkans;
  const specs = result?.specificaties || [];
  const sterk = beoordeling?.niveau === 'sterk';

  // Prijs voor preview-kaart
  const prijs = Number(auto.verkoopPrijs) || (mp ? Math.round(((mp.min || 0) + (mp.max || 0)) / 2) : 0);
  const kmTekst = auto.kmStand ? `${Number(auto.kmStand).toLocaleString('nl-NL')} km` : '—';

  const copy = (txt, label) => { navigator.clipboard?.writeText(txt || ''); toast.success(`${label} ${t('gekopieerd', 'gekopieerd')}`); };
  const volledigeTekst = huidig ? `${huidig.titel || ''}\n\n${huidig.tekst || ''}\n\n${t('Vraagprijs', 'Vraagprijs')}: ${eur(prijs)}` : '';

  const deelWhatsApp = () => {
    if (!huidig) return;
    window.open(`https://wa.me/?text=${enc(volledigeTekst)}`, '_blank', 'noopener');
  };

  const downloadFotos = () => {
    if (!fotos.length) { toast.info(t('Geen foto\'s om te downloaden', 'Geen foto\'s om te downloaden')); return; }
    const basis = [auto.merk, auto.model, auto.kenteken].filter(Boolean).join('_').replace(/[^a-z0-9]+/gi, '_').replace(/^_|_$/g, '').toLowerCase() || 'auto';
    fotos.forEach((f, i) => {
      setTimeout(() => {
        const a = document.createElement('a');
        a.href = f.dataUrl; a.download = `${basis}_${i + 1}.jpg`;
        document.body.appendChild(a); a.click(); a.remove();
      }, i * 600);
    });
    toast.success(t('Foto\'s gedownload', 'Foto\'s gedownload'), { description: `${fotos.length} ${t('foto(\'s) opgeslagen', 'foto(\'s) opgeslagen')}` });
  };

  const plaatsOpFacebook = () => {
    const fb = result?.platforms?.facebook;
    const titel = fb?.titel || huidig?.titel || term || '';
    const tekst = fb?.tekst || huidig?.tekst || '';
    const prijsT = Number(prijs) > 0 ? `\n\n${t('Vraagprijs', 'Vraagprijs')}: ${eur(prijs)}` : '';
    navigator.clipboard?.writeText(`${titel}\n\n${tekst}${prijsT}`.trim());
    if (fotos.length) downloadFotos();
    window.open('https://www.facebook.com/marketplace/create/vehicle', '_blank', 'noopener');
    toast.success(t('Klaar om te plakken op Facebook', 'Klaar om te plakken op Facebook'), {
      description: t('Titel + tekst staan op je klembord; foto\'s zijn gedownload. Plak ze in Facebook Marketplace.', 'Titel + tekst staan op je klembord; foto\'s zijn gedownload. Plak ze in Facebook Marketplace.'),
    });
  };

  const exportPdf = () => {
    if (!huidig) return;
    const w = window.open('', '_blank');
    if (!w) { toast.error(t('Pop-up geblokkeerd', 'Pop-up geblokkeerd')); return; }
    const specRows = specs.map(s => `<tr><td style="color:#64748b;padding:3px 12px 3px 0">${s.label}</td><td style="font-weight:600">${s.waarde}</td></tr>`).join('');
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${huidig.titel || term}</title>
      <style>body{font-family:system-ui,Arial,sans-serif;color:#0f172a;max-width:720px;margin:30px auto;padding:0 20px;line-height:1.5}
      h1{font-size:22px;margin:0 0 4px} .price{font-size:24px;font-weight:800;color:#16a34a;margin:8px 0 16px}
      .txt{white-space:pre-wrap;font-size:14px} table{font-size:13px;border-collapse:collapse;margin-top:16px}
      .muted{color:#94a3b8;font-size:11px;margin-top:24px}</style></head><body>
      <h1>${huidig.titel || term}</h1><div class="price">${eur(prijs)}</div>
      <div class="txt">${(huidig.tekst || '').replace(/</g, '&lt;')}</div>
      <h3 style="margin-top:20px">${t('Specificaties', 'Specificaties')}</h3><table>${specRows}</table>
      <div class="muted">MonteurCentraal · ${new Date().toLocaleDateString('nl-NL')}</div>
      </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 350);
  };

  // ----- KPI-kaart helper -----
  const Kpi = ({ icon, kleur, label, waarde, sub, testid }) => (
    <div data-testid={testid} style={{ flex: 1, minWidth: 180, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <div style={{ width: 36, height: 36, borderRadius: 9, background: `${kleur}1a`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: kleur, flexShrink: 0 }}>{icon}</div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 11.5, color: '#94a3b8', fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 17, fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>{waarde}</div>
        {sub && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );

  return (
    <div className="cs-modal-overlay" onClick={onClose} style={{ alignItems: 'flex-start', overflowY: 'auto', padding: '24px 16px' }}>
      <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 1080, width: '96vw' }} data-testid="verkoopadvertentie-modal">
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 44, height: 44, borderRadius: 11, background: 'linear-gradient(135deg,#7c3aed,#a855f7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Car size={22} color="#fff" />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 19, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Sparkles size={17} color="#7c3aed" /> {t('Verkoopadvertentie', 'Verkoopadvertentie')}
              </h3>
              <div style={{ fontSize: 12.5, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 8 }}>
                {[auto.merk, auto.model, auto.bouwjaar].filter(Boolean).join(' ').toUpperCase()}{auto.kenteken ? ` · ${auto.kenteken}` : ''}
                {beoordeling && (
                  <span data-testid="advert-klaar-badge" style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: sterk ? '#dcfce7' : '#fef3c7', color: sterk ? '#15803d' : '#b45309' }}>
                    {sterk ? t('Klaar om te plaatsen', 'Klaar om te plaatsen') : t('Kan beter', 'Kan beter')}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button className="cs-btn ghost" onClick={onClose} data-testid="advert-close"><X size={16} /></button>
        </div>

        {/* KPI-kaarten */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
          <Kpi testid="kpi-marktprijs" icon={<TrendingUp size={18} />} kleur="#7c3aed"
            label={t('Indicatieve marktprijs', 'Indicatieve marktprijs')}
            waarde={mp ? `${eur(mp.min)} – ${eur(mp.max)}` : (loading ? '…' : '—')}
            sub={kmTekst !== '—' ? t('Gebaseerd op km & onderhoud', 'Gebaseerd op km & onderhoud') : null} />
          <Kpi testid="kpi-conditie" icon={<ShieldCheck size={18} />} kleur="#16a34a"
            label={t('Voertuigconditie', 'Voertuigconditie')}
            waarde={result?.conditie || (loading ? '…' : '—')}
            sub={auto.kostenposten?.length ? t('Recent onderhouden', 'Recent onderhouden') : null} />
          <Kpi testid="kpi-km" icon={<Gauge size={18} />} kleur="#2563eb"
            label={t('Kilometerstand', 'Kilometerstand')} waarde={kmTekst}
            sub={t('Logisch voor bouwjaar', 'Logisch voor bouwjaar')} />
          <Kpi testid="kpi-verkoopkans" icon={<TrendingUp size={18} />} kleur="#ea580c"
            label={t('Verkoopkans', 'Verkoopkans')}
            waarde={vk ? (typeof vk.score === 'number' && vk.score > 0 ? `${vk.score}%` : vk.niveau) : (loading ? '…' : '—')}
            sub={vk ? [vk.niveau && typeof vk.score === 'number' && vk.score > 0 ? vk.niveau : null, vk.verkooptijd].filter(Boolean).join(' · ') : null} />
        </div>

        {/* Actieknoppen */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 16 }}>
          {links.map(l => (
            <a key={l.id} href={l.url} target="_blank" rel="noopener noreferrer" data-testid={`advert-link-${l.id}`} style={actieBtn(l.kleur)}><ExternalLink size={15} /> {l.label}</a>
          ))}
          <button onClick={deelWhatsApp} disabled={!huidig} data-testid="advert-whatsapp" style={{ ...actieBtn('#fff'), color: '#16a34a', border: '1px solid #e2e8f0', cursor: huidig ? 'pointer' : 'not-allowed' }}><MessageCircle size={15} /> {t('Deel via WhatsApp', 'Deel via WhatsApp')}</button>
          <button onClick={plaatsOpFacebook} disabled={!huidig} data-testid="advert-facebook-plaats" style={{ ...actieBtn('#1877f2'), cursor: huidig ? 'pointer' : 'not-allowed' }}><ExternalLink size={15} /> {t('Plaats op Facebook', 'Plaats op Facebook')}</button>
        </div>

        {/* Body: tabs + preview */}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.4fr) minmax(0,1fr)', gap: 16 }}>
          {/* Linkerkolom */}
          <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 14 }}>
            <div style={{ display: 'flex', gap: 16, borderBottom: '1px solid #e2e8f0', marginBottom: 12 }}>
              {[['tekst', t('Advertentietekst', 'Advertentietekst')], ['specs', t('Specificaties', 'Specificaties')], ['foto', `${t('Foto\'s', 'Foto\'s')} (${fotos.length})`], ['prijs', t('Prijsanalyse', 'Prijsanalyse')]].map(([id, lbl]) => (
                <button key={id} onClick={() => setTab(id)} data-testid={`advert-tab-${id}`}
                  style={{ padding: '6px 2px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13.5, fontWeight: 600, color: tab === id ? '#7c3aed' : '#64748b', borderBottom: tab === id ? '2px solid #7c3aed' : '2px solid transparent', marginBottom: -1 }}>
                  {lbl}
                </button>
              ))}
            </div>

            {loading && !result ? (
              <div className="cs-empty" data-testid="advert-loading"><RefreshCw size={16} className="spin" style={{ verticalAlign: -3 }} /> {t('AI schrijft je advertentie…', 'AI schrijft je advertentie…')}</div>
            ) : tab === 'tekst' ? (
              <div data-testid="advert-content" style={{ position: 'relative' }}>
                {loading && (
                  <div data-testid="advert-busy" style={{ position: 'absolute', inset: 0, zIndex: 5, background: 'rgba(255,255,255,0.72)', backdropFilter: 'blur(1px)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: 'var(--cs-text-muted)' }}><RefreshCw size={15} className="spin" /> {t('AI werkt de advertentie bij…', 'AI werkt de advertentie bij…')}</span>
                  </div>
                )}
                {/* platform-tabs */}
                <div style={{ display: 'flex', gap: 4, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 3, marginBottom: 12 }}>
                  {PLATFORMS.map(p => (
                    <button key={p.id} onClick={() => setPlatform(p.id)} data-testid={`advert-platform-${p.id}`}
                      style={{ flex: 1, padding: '6px 6px', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, background: platform === p.id ? p.kleur : 'transparent', color: platform === p.id ? '#fff' : '#64748b' }}>
                      {p.naam}
                    </button>
                  ))}
                </div>
                <label className="cs-section-title">{t('Titel', 'Titel')}</label>
                <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                  <input className="cs-input" value={huidig?.titel || ''} readOnly data-testid="advert-titel" style={{ fontWeight: 600 }} />
                  <button className="cs-btn" onClick={() => copy(huidig?.titel, t('Titel', 'Titel'))} data-testid="advert-copy-titel" title={t('Kopieer', 'Kopieer')}><Copy size={14} /></button>
                </div>
                <label className="cs-section-title">{t('Beschrijving', 'Beschrijving')}</label>
                <textarea className="cs-input" value={huidig?.tekst || ''} readOnly rows={11} data-testid="advert-tekst" style={{ resize: 'vertical', lineHeight: 1.5 }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  <button className="cs-btn" onClick={() => genereer()} disabled={loading} data-testid="advert-regenerate"><RefreshCw size={14} className={loading ? 'spin' : ''} /> {t('Opnieuw genereren', 'Opnieuw genereren')}</button>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12.5, color: 'var(--cs-text-muted)' }}><Globe size={14} /> {t('AI vertalen', 'AI vertalen')}</span>
                    <select className="cs-input" style={{ maxWidth: 180 }} value={adLang} data-testid="advert-taal" disabled={loading}
                      onChange={e => vertaalNaar(e.target.value)}>
                      {AD_TALEN.map(l => <option key={l.code} value={l.code}>{`${l.vlag} ${l.label}`}</option>)}
                    </select>
                    <select className="cs-input" style={{ maxWidth: 160 }} value={stijl} data-testid="advert-stijl" disabled={loading}
                      onChange={e => { setStijl(e.target.value); genereer(e.target.value); }}>
                      {STIJLEN.map(s => <option key={s.id} value={s.id}>{`${t('Stijl', 'Stijl')}: ${s.label}`}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            ) : tab === 'specs' ? (
              <div data-testid="advert-specs">
                {specs.length === 0 ? <div className="cs-empty">{t('Geen specificaties', 'Geen specificaties')}</div> : (
                  <table style={{ width: '100%', fontSize: 13.5, borderCollapse: 'collapse' }}>
                    <tbody>
                      {specs.map((s, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }} data-testid={`spec-${slug(s.label)}`}>
                          <td style={{ color: '#64748b', padding: '7px 12px 7px 0', whiteSpace: 'nowrap' }}>{s.label}</td>
                          <td style={{ fontWeight: 600, padding: '7px 0' }}>{s.waarde}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ) : tab === 'foto' ? (
              <div data-testid="advert-foto">
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple
                  onChange={onFotosKiezen} style={{ display: 'none' }} data-testid="advert-foto-input" />
                <button className="cs-btn" onClick={() => fileRef.current?.click()} disabled={fotos.length >= 6} data-testid="advert-foto-upload"
                  style={{ width: '100%', justifyContent: 'center', padding: '14px', border: '2px dashed #cbd5e1', background: '#f8fafc', marginBottom: 12 }}>
                  <Upload size={16} /> {fotos.length === 0 ? t('Foto\'s uploaden voor waardebepaling', 'Foto\'s uploaden voor waardebepaling') : t('Meer foto\'s toevoegen', 'Meer foto\'s toevoegen')} ({fotos.length}/6)
                </button>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
                  <Camera size={13} style={{ verticalAlign: -2 }} /> {t('De AI bekijkt je foto\'s en herkent kenmerken (velgen, trekhaak, schade, staat) voor de advertentie en waardebepaling.', 'De AI bekijkt je foto\'s en herkent kenmerken (velgen, trekhaak, schade, staat) voor de advertentie en waardebepaling.')}
                </div>
                {fotos.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 14 }}>
                    {fotos.map((f, i) => (
                      <div key={i} style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', aspectRatio: '4/3', background: '#e2e8f0' }} data-testid={`advert-foto-thumb-${i}`}>
                        <img src={f.dataUrl} alt={`foto ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <button onClick={() => verwijderFoto(i)} data-testid={`advert-foto-del-${i}`}
                          style={{ position: 'absolute', top: 4, right: 4, width: 26, height: 26, borderRadius: 6, border: 'none', background: 'rgba(220,38,38,.92)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {fotoanalyse ? (
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 12 }} data-testid="advert-fotoanalyse">
                    <div className="cs-section-title"><Eye size={14} style={{ verticalAlign: -2 }} /> {t('AI-fotoanalyse', 'AI-fotoanalyse')}</div>
                    {(fotoanalyse.kenmerken || []).length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '6px 0 10px' }}>
                        {fotoanalyse.kenmerken.map((k, i) => (
                          <span key={i} style={{ fontSize: 12, fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: '#ede9fe', color: '#6d28d9' }}>{k}</span>
                        ))}
                      </div>
                    )}
                    {fotoanalyse.staat && <Rij label={t('Geschatte staat', 'Geschatte staat')} val={fotoanalyse.staat} />}
                    {fotoanalyse.schade && <Rij label={t('Zichtbare schade', 'Zichtbare schade')} val={fotoanalyse.schade} />}
                    {fotoanalyse.waarde_invloed && <div style={{ fontSize: 12.5, color: '#334155', marginTop: 6 }}>{fotoanalyse.waarde_invloed}</div>}
                  </div>
                ) : fotos.length > 0 && !loading ? (
                  <div className="cs-empty">{t('Klik op "Opnieuw genereren" om de foto\'s te analyseren.', 'Klik op "Opnieuw genereren" om de foto\'s te analyseren.')}</div>
                ) : null}
              </div>
            ) : (
              <div data-testid="advert-prijs">
                {/* Marktprijs */}
                {mp && (
                  <div style={{ marginBottom: 14 }}>
                    <div className="cs-section-title">{t('Indicatieve marktprijs', 'Indicatieve marktprijs')}</div>
                    <div style={{ fontSize: 20, fontWeight: 800 }}>{eur(mp.min)} – {eur(mp.max)}</div>
                    {mp.toelichting && <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{mp.toelichting}</div>}
                  </div>
                )}
                {/* Vraagprijs-meter */}
                {mp && prijs > 0 && (mp.max > mp.min) && (
                  <div style={{ marginBottom: 16 }} data-testid="advert-prijsmeter">
                    <div className="cs-section-title">{t('Vraagprijs-meter', 'Vraagprijs-meter')}</div>
                    <div style={{ position: 'relative', height: 10, borderRadius: 999, background: 'linear-gradient(90deg,#16a34a,#facc15,#dc2626)', margin: '14px 0 6px' }}>
                      <div style={{ position: 'absolute', top: -7, left: `${Math.min(100, Math.max(0, ((prijs - mp.min) / (mp.max - mp.min)) * 100))}%`, transform: 'translateX(-50%)' }}>
                        <div style={{ width: 0, height: 0, borderLeft: '7px solid transparent', borderRight: '7px solid transparent', borderTop: '10px solid #0f172a' }} />
                        <div style={{ fontSize: 11, fontWeight: 700, background: '#0f172a', color: '#fff', padding: '1px 6px', borderRadius: 5, transform: 'translateX(-50%)', marginLeft: '50%', whiteSpace: 'nowrap' }}>{eur(prijs)}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: '#64748b' }}>
                      <span>{t('Goedkoop', 'Goedkoop')} · {eur(mp.min)}</span>
                      <span>{t('Duur', 'Duur')} · {eur(mp.max)}</span>
                    </div>
                  </div>
                )}
                {/* Concurrentie */}
                {concurrentie && (concurrentie.aantal_vergelijkbaar > 0 || concurrentie.gemiddelde_vraagprijs > 0) && (
                  <div style={{ marginBottom: 14, background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: 12 }} data-testid="advert-concurrentie">
                    <div className="cs-section-title" style={{ color: '#c2410c' }}>🔥 {t('Concurrentie (indicatie)', 'Concurrentie (indicatie)')}</div>
                    <div style={{ fontSize: 14, color: '#0f172a', marginBottom: 6 }}>
                      ± <b>{concurrentie.aantal_vergelijkbaar}</b> {t('vergelijkbare', 'vergelijkbare')} {[auto.merk, auto.model].filter(Boolean).join(' ')} {t('te koop', 'te koop')}
                    </div>
                    <Rij label={t('Gemiddelde vraagprijs', 'Gemiddelde vraagprijs')} val={eur(concurrentie.gemiddelde_vraagprijs)} />
                    {prijs > 0 && <Rij label={t('Jouw prijs', 'Jouw prijs')} val={eur(prijs)} bold kleur={prijs <= (concurrentie.gemiddelde_vraagprijs || prijs) ? '#16a34a' : '#dc2626'} />}
                    {concurrentie.toelichting && <div style={{ fontSize: 11.5, color: '#9a3412', marginTop: 6 }}>{concurrentie.toelichting}</div>}
                  </div>
                )}
                {/* Winstberekening */}
                {winst && (
                  <div style={{ marginBottom: 14, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 12 }} data-testid="advert-winst">
                    <div className="cs-section-title">{t('Winstberekening', 'Winstberekening')}</div>
                    <Rij label={t('Inkoop', 'Inkoop')} val={eur2(winst.inkoop)} />
                    <Rij label={t('Onderdelen / kosten', 'Onderdelen / kosten')} val={eur2(winst.kosten)} />
                    <Rij label={t('Vraagprijs', 'Vraagprijs')} val={eur2(winst.vraagprijs)} bold />
                    <div style={{ borderTop: '1px dashed #cbd5e1', marginTop: 6, paddingTop: 6 }}>
                      <Rij label={t('Verwachte winst', 'Verwachte winst')} val={eur2(winst.verwachteWinst)} bold
                        kleur={winst.verwachteWinst >= 0 ? '#16a34a' : '#dc2626'} />
                    </div>
                    {onderhoudswaarde && onderhoudswaarde.bedrag > 0 && (
                      <div style={{ marginTop: 8, fontSize: 12.5, color: '#15803d', display: 'flex', gap: 6 }} data-testid="advert-onderhoudswaarde">
                        🔧 {t('Recent onderhoud verhoogt de marktwaarde met', 'Recent onderhoud verhoogt de marktwaarde met')} <b>+{eur(onderhoudswaarde.bedrag)}</b>
                      </div>
                    )}
                    {onderhoudswaarde?.toelichting && <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 2 }}>{onderhoudswaarde.toelichting}</div>}
                  </div>
                )}
                {/* AI-beoordeling + verkoopscore */}
                {beoordeling && (
                  <div data-testid="advert-beoordeling">
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
                      {vk && typeof vk.score === 'number' && vk.score > 0 && (
                        <div data-testid="advert-verkoopscore" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 999, fontWeight: 700, fontSize: 13, background: '#dcfce7', color: '#15803d' }}>
                          🟢 {t('Verkoopkans', 'Verkoopkans')} {vk.score}%
                        </div>
                      )}
                      {vk?.verkooptijd && (
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 999, fontWeight: 600, fontSize: 13, background: '#eff6ff', color: '#1d4ed8' }}>
                          <Clock size={14} /> {t('Verkooptijd', 'Verkooptijd')} {vk.verkooptijd}
                        </div>
                      )}
                    </div>
                    {(beoordeling.sterke_punten || []).length > 0 && (
                      <div style={{ marginBottom: 8 }}>
                        {beoordeling.sterke_punten.map((p, i) => (
                          <div key={i} style={{ display: 'flex', gap: 7, fontSize: 13, color: '#334155', padding: '2px 0' }}><CheckCircle2 size={15} color="#16a34a" style={{ flexShrink: 0, marginTop: 1 }} /> {p}</div>
                        ))}
                      </div>
                    )}
                    {(beoordeling.verbeterpunten || []).length > 0 && (
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#b45309', marginBottom: 2 }}>{t('Verbeterpunten', 'Verbeterpunten')}</div>
                        {beoordeling.verbeterpunten.map((p, i) => (
                          <div key={i} style={{ display: 'flex', gap: 7, fontSize: 13, color: '#334155', padding: '2px 0' }}><AlertTriangle size={15} color="#f59e0b" style={{ flexShrink: 0, marginTop: 1 }} /> {p}</div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Rechterkolom: Marktplaats preview */}
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>{t('Preview Marktplaats', 'Preview Marktplaats')}</div>
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }} data-testid="advert-preview">
              <div style={{ position: 'relative', height: 170, background: 'linear-gradient(135deg,#475569,#1e293b)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {fotos[0] ? (
                  <img src={fotos[0].dataUrl} alt={t('auto')} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <Car size={64} color="#cbd5e1" />
                )}
                <span style={{ position: 'absolute', top: 10, left: 10, background: '#ea580c', color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 6 }}>{t('Te koop', 'Te koop')}</span>
                <span style={{ position: 'absolute', top: 10, right: 10, background: '#fff', borderRadius: 8, padding: 6, display: 'flex' }}><Heart size={15} color="#64748b" /></span>
                {auto.kenteken && <span style={{ position: 'absolute', bottom: 12, background: '#facc15', color: '#000', fontWeight: 800, fontFamily: 'ui-monospace,monospace', padding: '3px 12px', borderRadius: 5, border: '1px solid #ca8a04', letterSpacing: 1 }}>{auto.kenteken}</span>}
              </div>
              <div style={{ padding: 14 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', minHeight: 20 }}>{huidig?.titel || term || '—'}</div>
                <div style={{ fontSize: 20, fontWeight: 800, margin: '6px 0 10px' }}>{eur(prijs)}</div>
                <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#64748b', marginBottom: 12 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Clock size={13} /> {kmTekst}</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Calendar size={13} /> {auto.bouwjaar || '—'}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', fontSize: 12.5 }}>
                  {specs.slice(0, 6).map((s, i) => (
                    <div key={i}><div style={{ color: '#94a3b8' }}>{s.label}</div><div style={{ fontWeight: 600 }}>{s.waarde}</div></div>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>{t('Dit is een voorbeeld. De weergave kan afwijken van het echte platform.', 'Dit is een voorbeeld. De weergave kan afwijken van het echte platform.')}</div>
          </div>
        </div>

        {/* Showroom-publicatie */}
        <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap', alignItems: 'center', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 10, padding: 12 }} data-testid="advert-showroom-bar">
          <Globe size={18} color="#7c3aed" />
          <div style={{ flex: 1, minWidth: 160 }}>
            <div style={{ fontWeight: 700, fontSize: 13.5 }}>{t('Publieke showroom', 'Publieke showroom')}</div>
            <div style={{ fontSize: 12, color: '#6d28d9' }}>
              {showroom?.status === 'Gepubliceerd' ? t('Staat live op je showroom', 'Staat live op je showroom')
                : showroom?.status === 'Concept' ? t('Opgeslagen als concept', 'Opgeslagen als concept')
                : t('Eén klik en je auto staat online te koop', 'Eén klik en je auto staat online te koop')}
            </div>
          </div>
          {showroom ? (
            <>
              {showroom.status !== 'Gepubliceerd' && <button className="cs-btn primary" onClick={() => publiceer('Gepubliceerd')} disabled={publishing || !huidig} data-testid="advert-publiceer"><Globe size={14} /> {t('Publiceer', 'Publiceer')}</button>}
              <button className="cs-btn" onClick={verwijderUitShowroom} disabled={publishing} data-testid="advert-unpublish" style={{ color: '#dc2626' }}><Trash2 size={14} /> {t('Verwijder uit showroom', 'Verwijder uit showroom')}</button>
            </>
          ) : (
            <>
              <button className="cs-btn" onClick={() => publiceer('Concept')} disabled={publishing || !huidig} data-testid="advert-concept"><FileDown size={14} /> {t('Opslaan als concept', 'Opslaan als concept')}</button>
              <button className="cs-btn primary" onClick={() => publiceer('Gepubliceerd')} disabled={publishing || !huidig} data-testid="advert-publiceer"><Globe size={14} /> {t('Publiceer in showroom', 'Publiceer in showroom')}</button>
            </>
          )}
        </div>

        {/* Onderste actiebalk */}
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button className="cs-btn" onClick={exportPdf} disabled={!huidig} data-testid="advert-pdf"><FileDown size={14} /> {t('Exporteren (PDF)', 'Exporteren (PDF)')}</button>
          <button className="cs-btn" onClick={() => copy(`${links.mp}\n${links.as24}\n${links.gp}`, t('Deeplinks', 'Deeplinks'))} data-testid="advert-deeplink"><Link2 size={14} /> {t('Deeplinks kopiëren', 'Deeplinks kopiëren')}</button>
          <button className="cs-btn primary" onClick={() => copy(volledigeTekst, t('Advertentie', 'Advertentie'))} disabled={!huidig} data-testid="advert-copy-all"><Copy size={14} /> {t('Kopieer alles', 'Kopieer alles')}</button>
        </div>

        <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}

const actieBtn = (bg) => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '10px 12px',
  borderRadius: 9, background: bg, color: '#fff', fontWeight: 600, fontSize: 13, textDecoration: 'none', border: 'none', cursor: 'pointer',
});

const Rij = ({ label, val, bold, kleur }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13.5, padding: '3px 0' }}>
    <span style={{ color: '#64748b' }}>{label}</span>
    <span style={{ fontWeight: bold ? 700 : 500, color: kleur || '#0f172a' }}>{val}</span>
  </div>
);
